import { createContext, useCallback, useEffect, useReducer } from 'react';

export const AuthContext = createContext();

// Set when a login ends because its token expired, so the login page can say why.
export const SESSION_EXPIRED_KEY = 'sessionExpired';

export const authReducer = (state, action) => {
    switch (action.type) {
        case 'LOGIN':
        return {
            user: action.payload,
        };
        case 'LOGOUT':
        return {
            user: null,
        };
        default:
        return state;
    }

}

// Expiry time (ms since epoch) of a JWT, or null if it has none or can't be read.
const tokenExpiry = (token) => {
    try {
        const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const { exp } = JSON.parse(atob(payload));
        return typeof exp === 'number' ? exp * 1000 : null;
    } catch {
        return null;
    }
};

const isExpired = (user) => {
    const exp = user && user.token ? tokenExpiry(user.token) : null;
    return exp !== null && exp <= Date.now();
};

export const AuthContextProvider = ({ children }) => {
  // Read the stored login synchronously on first render. Loading it in an effect left
  // `user` null for the first render, so opening any page directly (e.g. /mask-history in
  // a new tab) redirected to /login and then on to /, losing the requested page.
  const [state, dispatch] = useReducer(authReducer, null, () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (isExpired(user)) {
        localStorage.removeItem('user');
        sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
        return { user: null };
      }
      return { user };
    } catch {
      return { user: null };
    }
  });
  console.log("AuthContext state: ", state)

  // Ends the login because the token is no longer accepted; the routes then send the user to /login.
  const expireSession = useCallback(() => {
    localStorage.removeItem('user');
    sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
    dispatch({ type: 'LOGOUT' });
  }, []);

  // Log out the moment the token expires. Timers can fire late after the computer sleeps,
  // so expiry is also re-checked whenever the page becomes visible or regains focus.
  useEffect(() => {
    const exp = state.user && state.user.token ? tokenExpiry(state.user.token) : null;
    if (exp === null) return undefined;
    const check = () => { if (Date.now() >= exp) expireSession(); };
    // setTimeout overflows above ~24.8 days; re-checking at that point is enough.
    const id = setTimeout(check, Math.min(Math.max(exp - Date.now(), 0), 2 ** 31 - 1));
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    return () => {
      clearTimeout(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
    };
  }, [state.user, expireSession]);

  return (
    <AuthContext.Provider
      value={{
        ...state, dispatch, expireSession
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
